import React, {
  useCallback, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useFeatureFlagsContext, useI18n, useRootContext } from '@jarvis/react-portal-addons';
import NotificationsWidget from '../NotificationsWidget';
import PrinterCarousel from '../PrinterCarousel';
import useUsersApiCall from '../../../hooks/useUsersApiCall';
import useOpenStoreForHpApp from '../../../hooks/useOpenStoreForHpApp';
import { InstallTheHpAppButton, publishEvent } from '../../../utils/analytics';
import {
  Button,
  Container,
  MessageContainer,
  NotificationsWidgetContainer,
  PrinterCarouselContainer,
  Title,
  TitleAndNotificationsWrapper,
} from './styles';

const Banner = ({ 'data-testid': dataTestId, deviceCache, showNotifications }) => {
  const [titleContainerHeight, setTitleContainerHeight] = useState(0);
  const [notificationsExpanded, setNotificationsExpanded] = useState(false);
  const { consolidatedPortalPrinterCarousel, consolidatedPortalGetAppButton } = useFeatureFlagsContext();
  const { stack, shell: { authProvider, store } } = useRootContext();
  const { t } = useI18n();
  const { openStoreForHpApp, hpAppUrl } = useOpenStoreForHpApp();
  const titleRef = useRef(null);

  const userInfosCall = useUsersApiCall({
    authProvider,
    stack,
    store,
  });

  const { data: userInfos } = userInfosCall;

  const {
    pending: isLoadingDevices,
    data: devices,
  } = deviceCache;

  const accountHasDevice = useMemo(() => devices && devices.length > 0, [devices]);

  const showNotificationsAndCarousel = useMemo(
    () => !isLoadingDevices && accountHasDevice,
    [accountHasDevice, isLoadingDevices],
  );

  const handleInstallTheHPAppButtonClick = useCallback(() => {
    publishEvent(InstallTheHpAppButton);
    openStoreForHpApp();
  }, [openStoreForHpApp]);

  useLayoutEffect(() => {
    function updateSize() {
      setTitleContainerHeight(titleRef.current?.offsetHeight || 0);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return (
    <Container
      data-testid={dataTestId}
      className={[
        notificationsExpanded ? 'expanded' : '',
        isLoadingDevices || accountHasDevice ? 'has-devices' : '',
      ].join(' ')}
    >
      <TitleAndNotificationsWrapper
        titleHeight={titleContainerHeight}
        className={[
          accountHasDevice ? 'has-devices' : '',
        ].join(' ')}
      >
        {accountHasDevice && showNotifications && (
          <NotificationsWidgetContainer
            data-testid="smart-notification-widget-mfe"
            className={notificationsExpanded ? 'expanded' : 'collapsed'}
          >
            <NotificationsWidget
              stack={stack} properties={{
                onCollapse: () => {
                  setNotificationsExpanded(false);
                },
                onExpand: () => {
                  setNotificationsExpanded(true);
                },
              }}
            />
          </NotificationsWidgetContainer>
        )}
        <Title
          className={[
            'title-regular',
            notificationsExpanded ? 'hide' : '',
            accountHasDevice ? 'has-devices' : '',
          ].join(' ')}
          data-testid="user-name"
          ref={titleRef}
        >
          {t('dashboard.header.welcome.option1', {
            name: userInfos?.data?.givenName,
            interpolation: { escapeValue: false },
          })}
        </Title>
      </TitleAndNotificationsWrapper>

      {showNotificationsAndCarousel && consolidatedPortalPrinterCarousel && (
        <PrinterCarouselContainer data-testid="printer-carousel-container">
          <PrinterCarousel devices={devices} stack={stack} />
        </PrinterCarouselContainer>
      )}

      {!isLoadingDevices && !accountHasDevice && (
        <MessageContainer>
          <p data-testid="connect-printer-title" className="subtitle-large">
            {t('dashboard.header.connectPrinter.title')}
          </p>
          <p data-testid="connect-printer-message" className="body-regular">
            {t('dashboard.header.connectPrinter.description')}
          </p>
          {consolidatedPortalGetAppButton && hpAppUrl
            && (
            <Button
              data-testid="connect-printer-button"
              appearance="primary"
              onClick={handleInstallTheHPAppButtonClick}
            >
              {t('dashboard.header.connectPrinter.button')}
            </Button>
            )}
        </MessageContainer>
      )}
    </Container>
  );
};

Banner.defaultProps = {
  'data-testid': null,
  showNotifications: true,
};

Banner.propTypes = {
  'data-testid': PropTypes.string,
  deviceCache: PropTypes.shape({
    pending: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.object),
    error: PropTypes.shape({
      message: PropTypes.string,
      code: PropTypes.number,
    }),
    isFetching: PropTypes.bool,
    forceFetch: PropTypes.func,
    makeApiCall: PropTypes.func,
    success: PropTypes.bool,
  }).isRequired,
  showNotifications: PropTypes.bool,
};

export default Banner;
